package com.narbase.letsgo.web.views.analytics

import com.narbase.kunafa.core.components.*
import com.narbase.kunafa.core.components.layout.LinearLayout
import com.narbase.kunafa.core.css.*
import com.narbase.kunafa.core.dimensions.dependent.matchParent
import com.narbase.kunafa.core.dimensions.dependent.wrapContent
import com.narbase.kunafa.core.dimensions.px
import com.narbase.kunafa.core.lifecycle.LifecycleOwner
import com.narbase.letsgo.web.common.AppColors
import com.narbase.letsgo.web.dto.analytics.AnalyticsConfigDto
import com.narbase.letsgo.web.theme.theme
import com.narbase.letsgo.web.translations.localized
import com.narbase.letsgo.web.utils.horizontalFiller
import com.narbase.letsgo.web.utils.scrollable.scrollable
import com.narbase.letsgo.web.utils.views.*
import com.narbase.letsgo.web.utils.views.customViews.customCheckBox

class AnalyticsManagementComponent : Component() {

    private val viewModel = AnalyticsManagementViewModel()

    private var settingsForm: LinearLayout? = null
    private var saveStatusText: TextView? = null

    private var isEnabled = false
    private var isAutocaptureEnabled = false
    private var isPageViewCaptured = false
    private var isPageLeaveCaptured = false
    private var isSessionRecordingEnabled = false
    private var isSurveysEnabled = false
    private var isDebugEnabled = false
    private var hostInput: TextInput? = null
    private var projectApiKeyInput: TextInput? = null
    private var defaultsDateInput: TextInput? = null

    override fun onViewMounted(lifecycleOwner: LifecycleOwner) {
        super.onViewMounted(lifecycleOwner)
        viewModel.getSettings()
    }

    override fun View?.getView() = view {
        id = "analyticsManagementRootView"
        style { matchParentDimensions }
        scrollable {
            style { matchParentDimensions }
            verticalLayout {
                style {
                    width = matchParent
                    height = wrapContent
                    padding = 32.px
                }
                textView {
                    text = "Analytics".localized()
                    style {
                        width = wrapContent
                        fontSize = 20.px
                        fontWeight = "bold"
                    }
                }
                settingsForm = verticalLayout {
                    style {
                        width = matchParent
                        marginTop = 12.px
                    }
                    withLoadingAndError(
                        uiState = viewModel.settingsUiState,
                        onRetryClicked = { viewModel.getSettings() },
                        onLoaded = { renderSettingsForm() },
                    )
                }
            }
        }
    }

    private fun renderSettingsForm() {
        val settings = viewModel.settings ?: return
        readSettingsIntoFields(settings)
        settingsForm?.apply {
            clearAllChildren()
            verticalLayout {
                style {
                    width = matchParent
                    this["gap"] = "12px"
                }
                customCheckBox("Enabled".localized(), isSelected = isEnabled) { isEnabled = it }
                hostInput = theme.labeledTextInput(this, "PostHog host".localized()) {
                    text = settings.host
                    placeholder = "https://posthog.carapp-africa.net"
                }
                projectApiKeyInput = theme.labeledTextInput(this, "PostHog project API key".localized()) {
                    text = settings.projectApiKey
                    placeholder = "phc_"
                }
                defaultsDateInput = theme.labeledTextInput(this, "PostHog defaults date".localized()) {
                    text = settings.defaultsDate.orEmpty()
                    placeholder = "2026-08-30"
                }
                customCheckBox("Autocapture".localized(), isSelected = isAutocaptureEnabled) { isAutocaptureEnabled = it }
                customCheckBox("Capture page views".localized(), isSelected = isPageViewCaptured) { isPageViewCaptured = it }
                customCheckBox("Capture page leaves".localized(), isSelected = isPageLeaveCaptured) { isPageLeaveCaptured = it }
                customCheckBox("Session recording".localized(), isSelected = isSessionRecordingEnabled) { isSessionRecordingEnabled = it }
                customCheckBox("Surveys".localized(), isSelected = isSurveysEnabled) { isSurveysEnabled = it }
                customCheckBox("Debug logging".localized(), isSelected = isDebugEnabled) { isDebugEnabled = it }
                saveSettingsButton()
            }
        }
    }

    private fun LinearLayout.saveSettingsButton() {
        horizontalLayout {
            style {
                width = matchParent
                marginTop = 8.px
            }
            theme.mainButton(this) {
                text = "Save settings".localized()
                onClick = { saveSettings() }
                withLoadingAndError(
                    uiState = viewModel.upsertUiState,
                    onRetryClicked = { saveSettings() },
                    onLoaded = { saveStatusText?.text = "Analytics settings saved".localized() },
                )
            }
            saveStatusText = textView {
                style {
                    width = wrapContent
                    marginStart = 12.px
                    alignSelf = Alignment.Center
                    color = AppColors.greenDark
                }
            }
            horizontalFiller()
        }
    }

    private fun readSettingsIntoFields(settings: AnalyticsConfigDto) {
        isEnabled = settings.isEnabled
        isAutocaptureEnabled = settings.isAutocaptureEnabled
        isPageViewCaptured = settings.isPageViewCaptured
        isPageLeaveCaptured = settings.isPageLeaveCaptured
        isSessionRecordingEnabled = settings.isSessionRecordingEnabled
        isSurveysEnabled = settings.isSurveysEnabled
        isDebugEnabled = settings.isDebugEnabled
    }

    private fun saveSettings() {
        saveStatusText?.text = ""
        viewModel.saveSettings(editedSettings())
    }

    private fun editedSettings() = AnalyticsConfigDto(
        isEnabled = isEnabled,
        host = hostInput?.text.orEmpty(),
        projectApiKey = projectApiKeyInput?.text.orEmpty(),
        defaultsDate = defaultsDateInput?.text?.ifBlank { null },
        isAutocaptureEnabled = isAutocaptureEnabled,
        isPageViewCaptured = isPageViewCaptured,
        isPageLeaveCaptured = isPageLeaveCaptured,
        isSessionRecordingEnabled = isSessionRecordingEnabled,
        isSurveysEnabled = isSurveysEnabled,
        isDebugEnabled = isDebugEnabled,
    )
}
